<template>
  <div class="p-4" data-private>
    <div class="flex items-start justify-between mb-4">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Supplemental Data Flat File</h2>
        <p class="text-sm text-gray-500">
          Care gaps closed by the quality team, formatted to the
          <span class="font-medium">{{ payer }}</span> supplemental-data file layout.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex rounded border border-gray-300 overflow-hidden text-sm">
          <button
            v-for="v in ['file', 'evidence']"
            :key="v"
            class="px-3 py-1.5"
            :class="view === v ? 'bg-slate-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="setView(v)"
          >
            {{ v === 'file' ? 'File' : 'Evidence' }}
          </button>
        </div>
        <label class="flex items-center gap-1 text-xs text-gray-600">
          <input v-model="includePayerConfirmed" type="checkbox" @change="reload" />
          Include gaps the payer already confirmed closed
        </label>
        <select
          v-model="payer"
          class="text-sm border border-gray-300 rounded px-2 py-1.5"
          @change="onPayerChange"
        >
          <option v-for="p in payers" :key="p" :value="p">{{ p }}</option>
        </select>
        <select
          v-if="view === 'file'"
          v-model="layout"
          class="text-sm border border-gray-300 rounded px-2 py-1.5"
          @change="loadRows"
        >
          <option v-for="o in layoutOptions" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>
        </select>
        <button
          v-if="view === 'file'"
          class="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="loading || rows.length === 0"
          @click="exportFile(spec.submissionFormat || 'txt')"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
          Export
        </button>
        <button
          v-if="view === 'file' && !spec.submissionFormat"
          class="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="loading || rows.length === 0"
          title="Review copy for validating against source documents. Not the submission format."
          @click="exportFile('xlsx')"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
          Excel
        </button>
      </div>
    </div>

    <template v-if="view === 'file'">
    <div v-if="loading" class="py-16 text-center text-gray-400">Loading…</div>

    <div v-else-if="rows.length === 0" class="py-16 text-center">
      <p class="text-gray-500 font-medium">No submittable rows for {{ payer }}.</p>
      <div v-if="skipped" class="text-xs text-gray-500 mt-3 inline-block text-left">
        <p v-if="skipped.not_closed">
          · <span class="font-medium">{{ skipped.not_closed }}</span>
          chart review(s) did not close the gap (e.g. patient not found in EHR)
        </p>
        <p v-if="skipped.payer_confirmed">
          · <span class="font-medium">{{ skipped.payer_confirmed }}</span>
          already confirmed closed by the payer
        </p>
        <p v-if="skipped.no_code" class="text-amber-600">
          · <span class="font-medium">{{ skipped.no_code }}</span>
          closure(s) have no code in the Vytalize quality-codes file
        </p>
        <p v-if="skipped.no_dos">
          · <span class="font-medium">{{ skipped.no_dos }}</span> missing date of service
        </p>
        <p v-if="skipped.no_identity">
          · <span class="font-medium">{{ skipped.no_identity }}</span>
          missing member ID, name or DOB
        </p>
        <p v-if="skipped.no_address">
          · <span class="font-medium">{{ skipped.no_address }}</span> missing member address
        </p>
        <p v-if="skipped.no_provider">
          · <span class="font-medium">{{ skipped.no_provider }}</span>
          missing servicing provider NPI, TIN or taxonomy
        </p>
        <p v-if="skipped.wrong_layout" class="text-gray-400">
          · {{ skipped.wrong_layout }} belong to the other layout for this payer
        </p>
      </div>
    </div>

    <div v-else>
      <div class="text-xs text-gray-500 mb-2">
        {{ rows.length }} rows · {{ columns.length }} columns ({{ payer }} {{ layout }} layout)
        <span v-if="skippedNote" class="text-amber-600 ml-2">· excluded: {{ skippedNote }}</span>
      </div>
      <div class="overflow-auto border border-gray-200 rounded" style="max-height: calc(100vh - 260px)">
        <table class="min-w-full text-xs">
          <thead class="sticky top-0 bg-slate-800 text-white">
            <tr>
              <th
                v-for="col in columns"
                :key="col"
                class="text-left font-medium px-2 py-2 whitespace-nowrap"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in rows"
              :key="i"
              :class="i % 2 ? 'bg-gray-50' : 'bg-white'"
            >
              <td
                v-for="col in columns"
                :key="col"
                class="px-2 py-1 whitespace-nowrap text-gray-700"
              >
                {{ row[col] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </template>

    <div v-else class="flex gap-4">
      <div class="min-w-0" :style="{ width: drawerRow ? '50%' : '100%' }">
        <div class="flex items-center gap-2 mb-2">
          <button
            v-for="f in evidenceFilters"
            :key="f.value"
            class="text-xs rounded-full border px-3 py-1"
            :class="evidenceFilter === f.value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
            @click="evidenceFilter = f.value"
          >
            {{ f.label }} {{ f.count }}
          </button>
          <div class="ml-auto flex items-center gap-2">
            <button
              class="text-xs border border-gray-300 hover:bg-gray-50 text-gray-700 rounded px-3 py-1.5"
              @click="loadEvidence"
            >
              Refresh
            </button>
          </div>
        </div>
        <div v-if="evidenceLoading" class="py-16 text-center text-gray-400">Loading…</div>
        <div v-else-if="visibleEvidence.length === 0" class="py-16 text-center text-gray-500">No closures.</div>
        <div v-else class="overflow-auto border border-gray-200 rounded" style="max-height: calc(100vh - 260px)">
          <table class="min-w-full text-xs">
            <thead class="sticky top-0 bg-slate-800 text-white">
              <tr>
                <th v-for="h in EVIDENCE_HEADERS" :key="h" class="text-left font-medium px-2 py-2 whitespace-nowrap">{{ h }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, i) in visibleEvidence" :key="row.work_id">
                <tr :class="i % 2 ? 'bg-gray-50' : 'bg-white'">
                  <td class="px-2 py-1 whitespace-nowrap">
                    <span class="rounded px-2 py-0.5" :class="STATUS_CLASS[row.status]">{{ STATUS_LABEL[row.status] }}</span>
                  </td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="person">{{ [row.patient_last_name, row.patient_first_name].filter(Boolean).join(', ') || '—' }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700 tabular-nums sensitive" data-spoof-kind="dob">{{ row.patient_dob || '—' }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="identifier">{{ row.patient_identifier }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="contract">{{ row.contract_name }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700">{{ row.measure_code }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700 tabular-nums">{{ row.date_of_service }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700">{{ row.result }}</td>
                  <td class="px-2 py-1 whitespace-nowrap text-gray-700">{{ row.codes.join(', ') }}</td>
                  <td class="px-2 py-1 whitespace-nowrap">
                    <button
                      v-for="f in row.files"
                      :key="f.key"
                      class="text-blue-600 hover:underline mr-2"
                      @click="openPdf(f)"
                    >
                      PDF
                    </button>
                    <span v-if="!row.files.length" class="text-gray-400">No PDF (attested)</span>
                  </td>
                  <td class="px-2 py-1">
                    <div v-for="c in row.checks" :key="c" class="text-amber-700">{{ c }}</div>
                    <span v-if="row.validation && row.validation.status === 'running'" class="text-gray-500">Checking…</span>
                    <span v-else-if="row.validation && row.validation.status === 'failed'" class="text-red-700" :title="row.validation.error">Check failed</span>
                    <span
                      v-else-if="row.validation"
                      class="rounded px-2 py-0.5"
                      :class="VERDICT_CLASS[row.validation.verdict]"
                      :title="(row.validation.issues || []).join('\n')"
                    >
                      {{ VERDICT_LABEL[row.validation.verdict] }}
                    </span>
                    <span v-else-if="row.files.length" class="text-gray-400">Not validated</span>
                    <div v-if="row.reason" class="text-gray-500">{{ row.reason }}</div>
                  </td>
                  <td class="px-2 py-1 whitespace-nowrap">
                    <button class="text-blue-600 hover:underline mr-2" @click="openPatient(row)">Open patient</button>
                    <button class="text-blue-600 hover:underline mr-2" @click="startCorrect(row)">Correct</button>
                    <button
                      v-if="row.files.length && row.status !== 'blocked'"
                      class="text-blue-600 hover:underline mr-2"
                      :disabled="row.validation && row.validation.status === 'running'"
                      @click="validateOne(row)"
                    >
                      Validate
                    </button>
                    <button v-if="row.status === 'excluded'" class="text-blue-600 hover:underline" @click="restore(row)">Restore</button>
                    <button v-else-if="row.status === 'on_file'" class="text-red-600 hover:underline" @click="startExclude(row)">Exclude</button>
                  </td>
                </tr>
                <tr v-if="correction && correction.work_id === row.work_id" class="bg-blue-50">
                  <td :colspan="EVIDENCE_HEADERS.length" class="px-2 py-2">
                    <div class="flex items-center gap-2 flex-wrap">
                      <label class="text-xs text-gray-600 flex items-center gap-1">
                        Service date
                        <input
                          v-model="correction.date_of_service"
                          type="date"
                          class="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                        />
                      </label>
                      <label class="text-xs text-gray-600 flex items-center gap-1">
                        Result / Value
                        <input
                          v-model="correction.result"
                          type="text"
                          placeholder="e.g. 124/78, 7.2, colonoscopy"
                          class="sensitive text-xs border border-gray-300 rounded px-2 py-1 bg-white w-40"
                        />
                      </label>
                      <label class="text-xs text-gray-600 flex items-center gap-1">
                        Code override
                        <input
                          v-model="correction.codes"
                          type="text"
                          placeholder="CPT / CPT-II"
                          class="sensitive text-xs border border-gray-300 rounded px-2 py-1 bg-white w-32"
                        />
                      </label>
                      <button
                        class="text-xs bg-blue-600 text-white rounded px-3 py-1 disabled:opacity-50"
                        :disabled="savingCorrection"
                        @click="confirmCorrect"
                      >
                        {{ savingCorrection ? 'Saving…' : 'Save correction' }}
                      </button>
                      <button class="text-xs text-gray-600 hover:underline" @click="correction = null">Cancel</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="exclusion && exclusion.work_id === row.work_id" class="bg-red-50">
                  <td :colspan="EVIDENCE_HEADERS.length" class="px-2 py-2">
                    <div class="flex items-center gap-2">
                      <select v-model="exclusion.choice" class="text-xs border border-gray-300 rounded px-2 py-1">
                        <option v-for="r in EXCLUDE_REASONS" :key="r" :value="r">{{ r }}</option>
                      </select>
                      <input
                        v-if="exclusion.choice === 'Other'"
                        v-model="exclusion.other"
                        class="text-xs border border-gray-300 rounded px-2 py-1 w-64"
                        placeholder="Reason"
                      />
                      <button
                        class="text-xs bg-red-600 text-white rounded px-3 py-1 disabled:opacity-50"
                        :disabled="exclusion.choice === 'Other' && !exclusion.other.trim()"
                        @click="confirmExclude"
                      >
                        Exclude from file
                      </button>
                      <button class="text-xs text-gray-600 hover:underline" @click="exclusion = null">Cancel</button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-if="drawerRow"
        class="flex flex-col overflow-hidden"
        style="width: 50%; height: calc(100vh - 200px)"
      >
        <Patient_Drawer
          :patient-identifier="drawerRow.patient_identifier"
          :entity-id="drawerRow.entity_id"
          :patient-data="drawerPatientData"
          :patient-name="[drawerRow.patient_first_name, drawerRow.patient_last_name].filter(Boolean).join(' ')"
          :enabled-tabs="['details', 'gaps']"
          :default-tab="'gaps'"
          :show-header="true"
          @task-updated="loadEvidence(true)"
          @close="closeDrawer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
// Each payer has its own published file layout. Endpoints return the spec
// column list (plain strings, in spec order) plus rows keyed by those names.
const PAYER_SPECS = {
  Anthem: {
    endpoint: '/api/quality/supplemental-flat-file/',
    exportEndpoint: '/api/quality/supplemental-flat-file-export/',
    layouts: [
      { value: 'EMR', label: 'EMR / Encounter file' },
      { value: 'LAB', label: 'Lab file' },
    ],
    filename: (layout, stamp) => `Vytalize_${layout === 'LAB' ? 'LAB' : 'EMR'}_${stamp}_NS.txt`,
  },
  Aetna: {
    endpoint: '/api/quality/aetna-flat-file/',
    exportEndpoint: '/api/quality/aetna-flat-file-export/',
    layouts: [
      { value: 'MEDICAL', label: 'Medical file' },
      { value: 'LAB', label: 'Lab file' },
    ],
  },
  Fidelis: {
    endpoint: '/api/quality/fidelis-flat-file/',
    exportEndpoint: '/api/quality/fidelis-flat-file-export/',
    layouts: [
      { value: 'SERVICE', label: 'Service file' },
      { value: 'LAB', label: 'Lab file' },
    ],
    // Wellcare rejects the file outright on a wrong name. Fidelis assigned
    // Vytalize VYH_NY_SERVICE_* on the 2026-08-18 test review — no [CNC|ESD]
    // segment, despite the SuDS guide publishing one.
    filename: (layout, stamp) => `VYH_NY_${layout === 'LAB' ? 'LAB' : 'SERVICE'}_${stamp}.txt`,
  },
  Emblem: {
    endpoint: '/api/quality/emblem-flat-file/',
    exportEndpoint: '/api/quality/emblem-flat-file-export/',
    layouts: [
      { value: 'STANDARD', label: 'Standard supplemental file' },
    ],
    submissionFormat: 'xlsx',
    filename: (layout, stamp) => `Vytalize_EmblemHealth_Supplemental_${stamp}.xlsx`,
  },
};

const EVIDENCE_HEADERS = [
  'Status', 'Patient', 'DOB', 'Member ID', 'Contract', 'Measure', 'Service date',
  'Result', 'Codes', 'Evidence', 'Checks', '',
];
const STATUS_LABEL = { on_file: 'On file', blocked: 'Blocked', excluded: 'Excluded' };
const STATUS_CLASS = {
  on_file: 'bg-emerald-100 text-emerald-800',
  blocked: 'bg-amber-100 text-amber-800',
  excluded: 'bg-gray-200 text-gray-700',
};
const VERDICT_LABEL = { supports: 'Supports', does_not_support: 'Does not support', unreadable: 'Unreadable' };
const VERDICT_CLASS = {
  supports: 'bg-emerald-100 text-emerald-800',
  does_not_support: 'bg-red-100 text-red-800',
  unreadable: 'bg-amber-100 text-amber-800',
};
const EXCLUDE_REASONS = ['Invalid evidence', 'Wrong code', 'Wrong date', 'Wrong value', 'Other'];

export default {
  name: 'Quality_FlatFile',
  components: { Patient_Drawer },
  data() {
    return {
      EVIDENCE_HEADERS,
      STATUS_LABEL,
      STATUS_CLASS,
      VERDICT_LABEL,
      VERDICT_CLASS,
      EXCLUDE_REASONS,
      view: 'file',
      includePayerConfirmed: false,
      evidence: [],
      evidenceLoading: false,
      evidenceFilter: 'all',
      pollTimer: null,
      exclusion: null,
      correction: null,
      savingCorrection: false,
      drawerRow: null,
      loading: false,
      columns: [],
      rows: [],
      skipped: null,
      payer: 'Aetna',
      payers: Object.keys(PAYER_SPECS),
      layout: 'MEDICAL',
    };
  },
  computed: {
    spec() {
      return PAYER_SPECS[this.payer];
    },
    layoutOptions() {
      return this.spec.layouts;
    },
    needsAttention() {
      return this.evidence.filter(r => r.status === 'on_file'
        && (r.checks.length || (r.validation && r.validation.verdict !== 'supports')));
    },
    evidenceFilters() {
      const count = status => this.evidence.filter(r => r.status === status).length;
      return [
        { value: 'all', label: 'All', count: this.evidence.length },
        { value: 'attention', label: 'Needs attention', count: this.needsAttention.length },
        { value: 'excluded', label: 'Excluded', count: count('excluded') },
        { value: 'blocked', label: 'Blocked', count: count('blocked') },
      ];
    },
    visibleEvidence() {
      if (this.evidenceFilter === 'attention') return this.needsAttention;
      if (this.evidenceFilter === 'all') return this.evidence;
      return this.evidence.filter(r => r.status === this.evidenceFilter);
    },
    drawerPatientData() {
      const r = this.drawerRow;
      if (!r) return null;
      return {
        bene_first_name: r.patient_first_name,
        bene_last_name: r.patient_last_name,
        bene_dob: r.patient_dob,
        contract_name: r.contract_name,
      };
    },
    skippedTotal() {
      if (!this.skipped) return 0;
      return Object.values(this.skipped).reduce((a, b) => a + (b || 0), 0);
    },
    skippedNote() {
      const s = this.skipped;
      if (!s) return '';
      const parts = [];
      if (s.payer_confirmed) parts.push(`${s.payer_confirmed} already confirmed closed by the payer`);
      if (s.excluded) parts.push(`${s.excluded} excluded in Evidence`);
      if (s.no_code) parts.push(`${s.no_code} no sourced code`);
      if (s.no_dos) parts.push(`${s.no_dos} no date of service`);
      if (s.no_identity) parts.push(`${s.no_identity} incomplete member identity`);
      if (s.no_address) parts.push(`${s.no_address} no member address`);
      if (s.no_provider) parts.push(`${s.no_provider} incomplete servicing provider`);
      return parts.join(', ');
    },
  },
  mounted() {
    this.loadRows();
  },
  beforeUnmount() {
    clearTimeout(this.pollTimer);
  },
  methods: {
    onPayerChange() {
      this.layout = this.spec.layouts[0].value;
      this.closeDrawer();
      this.exclusion = null;
      if (this.view === 'evidence') this.loadEvidence();
      else this.loadRows();
    },
    reload() {
      if (this.view === 'evidence') this.loadEvidence();
      else this.loadRows();
    },
    setView(view) {
      this.view = view;
      if (view === 'evidence') this.loadEvidence();
      else this.loadRows();
    },
    async loadEvidence(silent = false) {
      clearTimeout(this.pollTimer);
      if (silent !== true) this.evidenceLoading = true;
      try {
        const res = await this.$axios.post('/api/quality/flat-file-evidence/', {
          payer: this.payer.toLowerCase(),
          include_payer_confirmed: this.includePayerConfirmed,
        });
        this.evidence = res.data.rows || [];
      } catch (e) {
        this.evidence = [];
      } finally {
        this.evidenceLoading = false;
      }
      if (this.view === 'evidence' && this.evidence.some(r => r.validation && r.validation.status === 'running')) {
        this.pollTimer = setTimeout(() => this.loadEvidence(true), 5000);
      }
    },
    async openPdf(file) {
      const res = await this.$axios.get('/api/media/', { params: { key: file.key }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
    },
    openPatient(row) {
      this.drawerRow = row;
    },
    closeDrawer() {
      this.drawerRow = null;
    },
    startExclude(row) {
      this.correction = null;
      this.exclusion = { work_id: row.work_id, choice: EXCLUDE_REASONS[0], other: '' };
    },
    startCorrect(row) {
      this.exclusion = null;
      const currentCodes = (row.codes || []).join(', ');
      this.correction = {
        work_id: row.work_id,
        date_of_service: (row.date_of_service || '').slice(0, 10),
        result: row.result || '',
        codes: currentCodes,
        original_codes: currentCodes,
      };
    },
    async confirmCorrect() {
      if (!this.correction || this.savingCorrection) return;
      this.savingCorrection = true;
      try {
        const payload = {
          work_id: this.correction.work_id,
          date_of_service: this.correction.date_of_service,
          result: this.correction.result,
        };
        if ((this.correction.codes || '').trim() !== (this.correction.original_codes || '').trim()) {
          payload.codes = this.correction.codes;
        }
        await this.$axios.post('/api/quality/flat-file-correct/', payload);
        this.correction = null;
        await this.loadEvidence();
      } finally {
        this.savingCorrection = false;
      }
    },
    async confirmExclude() {
      const { work_id, choice, other } = this.exclusion;
      await this.$axios.post('/api/quality/flat-file-exclude/', {
        work_id,
        reason: choice === 'Other' ? other.trim() : choice,
      });
      this.exclusion = null;
      await this.loadEvidence();
    },
    async restore(row) {
      await this.$axios.post('/api/quality/flat-file-restore/', { work_id: row.work_id });
      await this.loadEvidence();
    },
    async validateOne(row) {
      await this.$axios.post('/api/quality/flat-file-validate/', { work_id: row.work_id });
      await this.loadEvidence(true);
    },
    async loadRows() {
      this.loading = true;
      try {
        const res = await this.$axios.post(this.spec.endpoint, {
          layout: this.layout,
          include_payer_confirmed: this.includePayerConfirmed,
        });
        this.columns = res.data.columns || [];
        this.rows = res.data.rows || [];
        this.skipped = res.data.skipped || null;
      } catch (e) {
        this.columns = [];
        this.rows = [];
        this.skipped = null;
      } finally {
        this.loading = false;
      }
    },
    async exportFile(format = 'txt') {
      try {
        const res = await this.$axios.post(
          this.spec.exportEndpoint,
          { layout: this.layout, format, include_payer_confirmed: this.includePayerConfirmed },
          { responseType: 'blob' },
        );
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const cd = res.headers && (res.headers['content-disposition']
          || (res.headers.get && res.headers.get('content-disposition')));
        const m = cd && /filename="?([^"]+)"?/.exec(cd);
        const fallback = format === 'xlsx' && !this.spec.submissionFormat
          ? `${this.payer}_${this.layout}_${stamp}_REVIEW.xlsx`
          : (this.spec.filename
            ? this.spec.filename(this.layout, stamp)
            : `${this.payer}_${this.layout}_${stamp}.txt`);
        const fname = m ? m[1] : fallback;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // surfaced via the empty-state; nothing to do here
      }
    },
  },
};
</script>
